/*
  aness-hero-slideshow.js — original slideshow driver for the Aness Hero section.
  No dependencies. Progressive enhancement: without JS, slide 1 stays visible.
  <aness-hero-slideshow data-autoplay="true|false" data-interval="3000">
*/
(function () {
  'use strict';

  if (customElements.get('aness-hero-slideshow')) return;

  class AnessHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-aness-slide]'));
      this.indicators = Array.prototype.slice.call(this.querySelectorAll('[data-aness-indicator]'));
      this.fills = Array.prototype.slice.call(this.querySelectorAll('[data-aness-progress-fill]'));

      if (this.slides.length < 2) return;

      this.index = 0;
      this.timer = null;
      this.paused = false;
      this.interval = parseInt(this.dataset.interval, 10) || 3000;
      this.autoplay = this.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._onVisibility = () => {
        this.setPaused(document.hidden);
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      // pause on hover — pointer devices only
      if (window.matchMedia('(hover: hover)').matches) {
        this._onEnter = () => this.setPaused(true);
        this._onLeave = () => this.setPaused(false);
        this.addEventListener('mouseenter', this._onEnter);
        this.addEventListener('mouseleave', this._onLeave);
      }

      this.indicators.forEach((btn, i) => {
        btn.addEventListener('click', () => this.goTo(i));
      });

      // Theme Editor: show the selected slide and hold the autoplay
      if (window.Shopify && window.Shopify.designMode) {
        this._onBlockSelect = (event) => {
          const slide = event.target && event.target.closest && event.target.closest('[data-aness-slide]');
          const i = this.slides.indexOf(slide);
          if (i >= 0) {
            this.goTo(i);
            this.setPaused(true);
          }
        };
        this._onBlockDeselect = () => this.setPaused(false);
        document.addEventListener('shopify:block:select', this._onBlockSelect);
        document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
      }

      this.startCycle();
    }

    disconnectedCallback() {
      this.stopTimer();
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._onBlockSelect) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
      }
    }

    get autoplayEnabled() {
      return this.autoplay && !this.reducedMotion;
    }

    stopTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    startCycle() {
      if (!this.autoplayEnabled || this.paused) {
        this.setFillStatic();
        return;
      }
      this.animateFill(this.index);
      this.timer = setTimeout(() => {
        this.goTo((this.index + 1) % this.slides.length);
      }, this.interval);
    }

    goTo(i) {
      if (i === this.index && this.slides[i].classList.contains('is-active')) {
        this.stopTimer();
        this.startCycle();
        return;
      }
      this.stopTimer();
      this.slides[this.index].classList.remove('is-active');
      this.indicators[this.index].removeAttribute('aria-current');
      this.index = i;
      this.slides[i].classList.add('is-active');
      this.indicators[i].setAttribute('aria-current', 'true');
      this.resetFills();
      this.startCycle();
    }

    setPaused(paused) {
      if (this.paused === paused) return;
      this.paused = paused;
      if (paused) {
        this.stopTimer();
        this.freezeFill(this.index);
      } else {
        this.resetFills();
        this.startCycle();
      }
    }

    animateFill(i) {
      const fill = this.fills[i];
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.transform = 'scaleX(0)';
      void fill.offsetWidth; // reflow so the next transition starts from 0
      fill.style.transition = 'transform ' + this.interval + 'ms linear';
      fill.style.transform = 'scaleX(1)';
    }

    freezeFill(i) {
      const fill = this.fills[i];
      if (!fill) return;
      const current = window.getComputedStyle(fill).transform;
      fill.style.transition = 'none';
      if (current && current !== 'none') {
        fill.style.transform = current;
      }
    }

    resetFills() {
      this.fills.forEach((fill, j) => {
        fill.style.transition = 'none';
        fill.style.transform = j < this.index ? 'scaleX(1)' : 'scaleX(0)';
      });
    }

    setFillStatic() {
      // autoplay off / reduced motion: mark the active slide's bar as full
      this.fills.forEach((fill, j) => {
        fill.style.transition = 'none';
        fill.style.transform = j === this.index ? 'scaleX(1)' : 'scaleX(0)';
      });
    }
  }

  customElements.define('aness-hero-slideshow', AnessHeroSlideshow);
})();
